import Grid from '@mui/material/Unstable_Grid2';
import * as React from 'react';
import { Link, LinkProps, useLocation } from 'react-router-dom';
import Profile from 'src/assets/icons/account.svg';
import UsersGrants from 'src/assets/icons/entityIcons/users.svg';
import Feedback from 'src/assets/icons/feedback.svg';
import Billing from 'src/assets/icons/entityIcons/bill.svg';
import Kafka from 'src/assets/icons/entityIcons/kafka-nav.svg';
import VectorDB from 'src/assets/icons/entityIcons/vectordb-nav.svg';
import BitnimbusLogo from 'src/assets/logo/bitnimbus-logo.png';
import BitnimbusLogoSmall from 'src/assets/logo/bitnimbus-logo-small.svg';
import { BetaChip } from 'src/components/BetaChip/BetaChip';
import Divider from 'src/components/core/Divider';
import useAccountManagement from 'src/hooks/useAccountManagement';
import useFlags from 'src/hooks/useFlags';
import usePrefetch from 'src/hooks/usePreFetch';
import useStyles from './PrimaryNav.styles';
import { linkIsActive } from './utils';

type NavEntity =
  | 'Kafka'
  | 'Profile'
  | 'Billing'
  | 'Users & Grants'
  | 'Account'
  | 'Account';

interface PrimaryLink {
  display: NavEntity;
  href: string;
  attr?: { [key: string]: any };
  icon?: JSX.Element;
  activeLinks?: Array<string>;
  onClick?: (e: React.ChangeEvent<any>) => void;
  hide?: boolean;
  isBeta?: boolean;
  prefetchRequestFn?: () => void;
  prefetchRequestCondition?: boolean;
}

export interface Props {
  closeMenu: () => void;
  isCollapsed: boolean;
}

export const PrimaryNav = (props: Props) => {
  const { closeMenu, isCollapsed } = props;
  const { classes, cx } = useStyles();

  const flags = useFlags();
  const location = useLocation();

  const [enableObjectPrefetch, setEnableObjectPrefetch] = React.useState(false);

  const [
    enableMarketplacePrefetch,
    setEnableMarketplacePrefetch,
  ] = React.useState(false);

  const { _isManagedAccount, account } = useAccountManagement();

  const primaryLinkGroups: PrimaryLink[][] = React.useMemo(
    () => [
      [
        {
          display: 'Kafka',
          href: '/kafka',
          activeLinks: ['/kafka', '/kafka/create'],
          icon: <Kafka style={{ height: 25, paddingLeft: '4px' }} />,
        },
      ],
      [
        {
          display: 'VectorDB',
          href: '/vectorDB',
          activeLinks: ['/vectorDB', '/vectorDB/create'],
          icon: (
            <VectorDB
              style={{
                height: 30,
                width: 'auto',
                transform: 'translateX(-4px)',
              }}
            />
          ),
        },
      ],
      [
        {
          display: 'Billing',
          href: '/account/billing',
          icon: <Billing style={{ paddingLeft: '4px' }} />,
        },
      ],
      [
        {
          display: 'Users & Grants',
          href: '/account/users',
          icon: <UsersGrants style={{ paddingLeft: '4px' }} />,
        },
      ],
      [
        {
          display: 'Account',
          href: '/account',
          icon: <Profile />,
        },
      ],
      [
        {
          display: 'Feedback',
          href: 'https://forms.gle/mNVS1ikPeVcaqSvi9',
          icon: <Feedback />,
          external: true,
        },
      ],
    ],
    []
  );

  return (
    <Grid
      className={classes.menuGrid}
      container
      alignItems="flex-start"
      justifyContent="flex-start"
      direction="column"
      wrap="nowrap"
      spacing={0}
      component="nav"
      role="navigation"
      id="main-navigation"
    >
      <Grid>
        <div
          className={cx(classes.logoItemAkamai, {
            [classes.logoItemAkamaiCollapsed]: isCollapsed,
          })}
        >
          <Link
            to={`/`}
            onClick={closeMenu}
            aria-label="Bitnimbus - Dashboard"
            title="Bitnimbus - Dashboard"
            className={cx({
              [classes.logoContainer]: isCollapsed,
            })}
          >
            {isCollapsed ? (
              <BitnimbusLogoSmall />
            ) : (
              <img
                src={BitnimbusLogo}
                alt="bitnimbus-logo"
                width={160}
                className={cx(
                  {
                    [classes.logoAkamaiCollapsed]: isCollapsed,
                  },
                  classes.logo
                )}
              />
            )}
          </Link>
        </div>
      </Grid>
      <div
        className={cx({
          ['fade-in-table']: true,
          [classes.fadeContainer]: true,
        })}
      >
        {primaryLinkGroups.map((thisGroup, idx) => {
          const filteredLinks = thisGroup.filter((thisLink) => !thisLink.hide);
          if (filteredLinks.length === 0) {
            return null;
          }
          return (
            <div key={idx}>
              <Divider
                className={classes.divider}
                spacingTop={12}
                spacingBottom={12}
              />
              {filteredLinks.map((thisLink) => {
                const props = {
                  key: thisLink.display,
                  closeMenu,
                  isCollapsed,
                  locationSearch: location.search,
                  locationPathname: location.pathname,
                  ...thisLink,
                };

                // PrefetchPrimaryLink and PrimaryLink are two separate components because invocation of
                // hooks cannot be conditional. <PrefetchPrimaryLink /> is a wrapper around <PrimaryLink />
                // that includes the usePrefetch hook.
                return thisLink.prefetchRequestFn &&
                  thisLink.prefetchRequestCondition !== undefined ? (
                  <PrefetchPrimaryLink
                    {...props}
                    prefetchRequestFn={thisLink.prefetchRequestFn}
                    prefetchRequestCondition={thisLink.prefetchRequestCondition}
                  />
                ) : (
                  <PrimaryLink {...props} />
                );
              })}
            </div>
          );
        })}
      </div>
    </Grid>
  );
};

export default React.memo(PrimaryNav);

interface PrimaryLinkProps extends PrimaryLink {
  closeMenu: () => void;
  isBeta?: boolean;
  isCollapsed: boolean;
  locationSearch: string;
  locationPathname: string;
  external?: boolean;
  prefetchProps?: {
    onMouseEnter: LinkProps['onMouseEnter'];
    onMouseLeave: LinkProps['onMouseLeave'];
    onFocus: LinkProps['onFocus'];
    onBlur: LinkProps['onBlur'];
  };
}

const PrimaryLink = React.memo((props: PrimaryLinkProps) => {
  const { classes, cx } = useStyles();

  const {
    isBeta,
    isCollapsed,
    closeMenu,
    href,
    onClick,
    attr,
    activeLinks,
    icon,
    display,
    locationSearch,
    locationPathname,
    prefetchProps,
  } = props;

  const isActiveLink = Boolean(
    linkIsActive(href, locationSearch, locationPathname, activeLinks)
  );

  const child = (
    <React.Fragment>
      {icon && (
        <div className="icon" aria-hidden>
          {icon}
        </div>
      )}
      <p
        className={cx({
          [classes.linkItem]: true,
          primaryNavLink: true,
          hiddenWhenCollapsed: isCollapsed,
        })}
      >
        {display}
        {isBeta ? (
          <BetaChip className={classes.chip} color="primary" component="span" />
        ) : null}
      </p>
    </React.Fragment>
  );

  return props.external ? (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className={cx({
        [classes.listItem]: true,
        [classes.active]: isActiveLink,
        [classes.collapsedItem]: isCollapsed,
      })}
      aria-current={isActiveLink}
      data-testid={`nav-item-${display}`}
    >
      {child}
    </a>
  ) : (
    <Link
      to={href}
      onClick={(e: React.ChangeEvent<any>) => {
        closeMenu();
        if (onClick) {
          onClick(e);
        }
      }}
      {...prefetchProps}
      {...attr}
      className={cx({
        [classes.listItem]: true,
        [classes.active]: isActiveLink,
        [classes.collapsedItem]: isCollapsed,
      })}
      aria-current={isActiveLink}
      data-testid={`nav-item-${display}`}
    >
      {child}
    </Link>
  );
});

interface PrefetchPrimaryLinkProps {
  prefetchRequestFn: () => void;
  prefetchRequestCondition: boolean;
}

// Wrapper around PrimaryLink that includes the usePrefetchHook.
export const PrefetchPrimaryLink = React.memo(
  (props: PrimaryLinkProps & PrefetchPrimaryLinkProps) => {
    const { makeRequest, cancelRequest } = usePrefetch(
      props.prefetchRequestFn,
      props.prefetchRequestCondition
    );

    const prefetchProps: PrimaryLinkProps['prefetchProps'] = {
      onMouseEnter: makeRequest,
      onFocus: makeRequest,
      onMouseLeave: cancelRequest,
      onBlur: cancelRequest,
    };

    return <PrimaryLink {...props} prefetchProps={prefetchProps} />;
  }
);
